import * as RadioGroup from "@radix-ui/react-radio-group";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  cn,
  FormTable,
  FormTableBody,
  FormTableColumnCell,
  FormTableColumnRow,
  FormTableItem,
  useApiCall,
} from "@porchpass/ui";
import {
  getDealerPortalV1DealerIdLenders,
  patchDealerPortalV1DealerIdConfiguration,
  patchDealerPortalV1DealerIdLendersLenderId,
  type DomainLender,
} from "@porchpass/api-client";
import TableHeader from "./TableHeader";
import { useDealer } from "src/hooks/useDealer";
import { TotalCountBadge } from "src/components/TotalCountBadge";
import Toggle from "src/components/Toggle";
import CustomRadio from "src/components/CustomRadio";
import {
  HeaderContainer,
  HeaderTitleContainer,
  HeaderTitle,
  HeaderSubTitle,
} from "./LendersTable.styles";
import PrimaryBadge from "src/components/PrimaryBadge";

export type LenderType = {
  id: DomainLender;
  name: string;
  email?: string;
  enabled: boolean;
};

export default () => {
  const { dealer_id } = useDealer();
  const [lenders, setLenders] = useState<LenderType[]>([]);
  const [primaryLender, setPrimaryLender] = useState<DomainLender | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const lendersSnapshotRef = useRef<LenderType[]>([]);

  const loadLenders = useCallback(async () => {
    if (!dealer_id) throw new Error("No dealer ID");
    const response = await getDealerPortalV1DealerIdLenders(dealer_id);
    if (response.status !== 200) {
      throw new Error("Error fetching lenders");
    }
    return response.data;
  }, [dealer_id]);

  const lendersQuery = useApiCall(loadLenders, {
    toast: {
      error: "Error fetching lenders",
      useServerErrorMessage: true,
    },
    onSuccess: (data) => {
      const nextLenders = (data.lenders ?? []).flatMap((lender) =>
        lender.id
          ? [
              {
                id: lender.id,
                name: lender.name ?? "",
                email: lender.email,
                enabled: lender.enabled ?? false,
              },
            ]
          : [],
      );
      setLenders(nextLenders);
      setPrimaryLender(data.primary_lender ?? null);
      setTotalCount(nextLenders.length);
    },
    onError: (error) => {
      console.error("Error fetching lenders:", error);
    },
  });

  const updateLenderRequest = useCallback(
    async ({ id, enabled }: { id: DomainLender; enabled: boolean }) => {
      if (!dealer_id) throw new Error("No dealer ID");
      await patchDealerPortalV1DealerIdLendersLenderId(dealer_id, id, {
        enabled,
        default: enabled,
      });
      return enabled;
    },
    [dealer_id],
  );

  const lenderUpdate = useApiCall(updateLenderRequest, {
    toast: {
      success: "Lender updated successfully",
      error: "Error updating lender",
      useServerErrorMessage: true,
    },
    onError: (error) => {
      setLenders(lendersSnapshotRef.current);
      console.error("Error updating lender:", error);
    },
  });

  const updatePrimaryLenderRequest = useCallback(
    async (id: DomainLender) => {
      if (!dealer_id) throw new Error("No dealer ID");
      await patchDealerPortalV1DealerIdConfiguration(dealer_id, {
        primary_lender: id,
      });
      return id;
    },
    [dealer_id],
  );

  const primaryLenderUpdate = useApiCall(updatePrimaryLenderRequest, {
    toast: {
      success: "Primary lender updated",
      error: "Error updating primary lender",
      useServerErrorMessage: true,
    },
    onError: (error) => {
      console.error("Error updating primary lender:", error);
    },
  });

  useEffect(() => {
    if (!dealer_id) return;
    void lendersQuery.execute();
  }, [dealer_id, lendersQuery.execute]);

  const updateLender = (lender: LenderType, updates: Partial<LenderType>) => {
    const enabled = updates.enabled ?? lender.enabled;
    lendersSnapshotRef.current = lenders;

    setLenders((prev) =>
      prev.map((prevLender): LenderType =>
        prevLender.id === lender.id
          ? { ...prevLender, ...updates }
          : prevLender,
      ),
    );

    void lenderUpdate.execute({ id: lender.id, enabled });
  };

  const handleSortByStatus = () => {
    const newDirection = sortDirection === "asc" ? "desc" : "asc";

    const sortedLenders = [...lenders].sort((a, b) => {
      if (a.enabled === b.enabled) return 0;
      return newDirection === "asc" ? (a.enabled ? 1 : -1) : a.enabled ? -1 : 1;
    });

    setSortDirection(newDirection);
    setLenders(sortedLenders);
  };

  return (
    <>
      <HeaderContainer>
        <HeaderTitleContainer>
          <HeaderTitle>
            Lenders
            {totalCount ? (
              <TotalCountBadge>{totalCount}</TotalCountBadge>
            ) : null}
          </HeaderTitle>
          <HeaderSubTitle>
            Customize lenders or assign a <PrimaryBadge />
            lender.
          </HeaderSubTitle>
        </HeaderTitleContainer>
      </HeaderContainer>

      <FormTable layout="columns" columns="88px minmax(0, 1fr) 120px">
        <TableHeader
          onSortByStatus={handleSortByStatus}
          sortDirection={sortDirection}
        />
        {lenders.length > 0 && (
          <FormTableBody className="p-4">
            {lenders.map((lender) => {
              const isPrimary = lender.id === primaryLender;

              return (
                <FormTableColumnRow
                  key={lender.id}
                  className={cn(
                    isPrimary ? "rounded-lg bg-[#fffbeb]" : undefined,
                    "py-3 px-6",
                  )}
                >
                  <FormTableColumnCell>
                    <Toggle
                      selected={lender.enabled}
                      setSelected={(enabled) => {
                        updateLender(lender, { enabled });
                      }}
                      disabled={isPrimary}
                    />
                  </FormTableColumnCell>
                  <FormTableColumnCell>
                    <FormTableItem
                      title={lender.name}
                      description={
                        lender.email ? lender.email : "Email not provided"
                      }
                    />
                  </FormTableColumnCell>
                  <FormTableColumnCell>
                    {lender.enabled ? (
                      <RadioGroup.Root
                        name="primaryLender"
                        value={primaryLender || ""}
                        onValueChange={(id: string) => {
                          const lenderId = id as DomainLender;
                          setPrimaryLender(lenderId);
                          void primaryLenderUpdate.execute(lenderId);

                          const selectedLender = lenders.find(
                            (item) => item.id === lenderId,
                          );
                          if (selectedLender) {
                            updateLender(selectedLender, {
                              enabled: selectedLender.enabled,
                            });
                          }
                        }}
                      >
                        <CustomRadio
                          value={lender.id}
                          selected={isPrimary}
                          setSelected={() => setPrimaryLender(lender.id)}
                          ariaLabel="Select primary lender"
                        />
                      </RadioGroup.Root>
                    ) : null}
                  </FormTableColumnCell>
                </FormTableColumnRow>
              );
            })}
          </FormTableBody>
        )}
      </FormTable>
    </>
  );
};
